/**
 * 描边对话框（编辑→描边…，DESIGN.md §6.1 补充）。
 *
 * Photoshop 的描边对话框：宽度、颜色、位置（内部/居中/外部），下面跟着
 * 混合模式/不透明度——跟填充对话框（FillDialog.tsx）同一套"内容+混合"
 * 结构，混合模式同样只收可分离模式（原因见 FillDialog.tsx 文件头）。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal, on } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import { BLEND_MODE_OPTIONS } from '../layers/LayersPanel';
import { SEPARABLE_BLEND_MODES, type SeparableBlendMode } from '../../gpu/shaders/blend/modes';
import type { StrokePosition } from '../../app/stroke-edit';
import styles from './StrokeDialog.module.css';

const SEPARABLE_SET = new Set<string>(SEPARABLE_BLEND_MODES);
const BLEND_MODE_CHOICES = BLEND_MODE_OPTIONS.filter((option) => SEPARABLE_SET.has(option.value));

export interface StrokeRequest {
  readonly width: number;
  /** `#rrggbb`（原生颜色输入框的格式）。 */
  readonly color: string;
  readonly position: StrokePosition;
  readonly blendMode: SeparableBlendMode;
  /** 0–1。 */
  readonly opacity: number;
}

export interface StrokeDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onStroke: (request: StrokeRequest) => void;
  /** 默认描边色（一般是前景色）。 */
  readonly defaultColor: string;
}

export function StrokeDialog(props: StrokeDialogProps): JSX.Element {
  const [width, setWidth] = createSignal(1);
  const [pickedColor, setPickedColor] = createSignal(props.defaultColor);
  const [position, setPosition] = createSignal<StrokePosition>('inside');
  const [blendMode, setBlendMode] = createSignal<SeparableBlendMode>('normal');
  const [opacity, setOpacity] = createSignal(100);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 每次打开都重置——同 FillDialog 的纪律，组件实例贯穿应用生命周期。
  // 必须用 `on(() => props.open, ...)` 显式限定依赖只有 open：效果体里还
  // 读了 `props.defaultColor`，如果让 Solid 自动收集依赖，效果会连带订阅
  // defaultColor——而 main.tsx 传的 `defaultColor={(version(), ...)}` 会
  // 随应用里任何无关操作触发的全局 version 递增而改变，导致对话框开着的
  // 时候，用户刚填的宽度/位置会被这条重置效果在毫无预警的情况下悄悄弹回
  // 默认值（真实复现过：磁性选框描边描出来的环宽度对不上表单里填的数字，
  // 就是这个效果在用户填表单期间被无关的 version 变化重新触发了一次）。
  createEffect(
    on(
      () => props.open,
      (open) => {
        if (open) {
          setWidth(1);
          setPickedColor(props.defaultColor);
          setPosition('inside');
          setBlendMode('normal');
          setOpacity(100);
        }
      },
    ),
  );

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="stroke-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="描边"
          data-testid="stroke-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>描边</h2>

          <label class={styles.row}>
            <span class={styles.label}>宽度</span>
            <input
              type="number"
              class={styles.number}
              min="1"
              max="500"
              value={width()}
              data-testid="stroke-width"
              onInput={(event) => {
                setWidth(Math.max(1, Math.round(Number(event.currentTarget.value) || 1)));
              }}
            />
            <span class={styles.value}>px</span>
          </label>

          <label class={styles.row}>
            <span class={styles.label}>颜色</span>
            <input
              type="color"
              class={styles.colorInput}
              value={pickedColor()}
              data-testid="stroke-color"
              onInput={(event) => {
                setPickedColor(event.currentTarget.value);
              }}
            />
          </label>

          <label class={styles.row}>
            <span class={styles.label}>位置</span>
            <select
              class={styles.select}
              value={position()}
              data-testid="stroke-position"
              onChange={(event) => {
                setPosition(event.currentTarget.value as StrokePosition);
              }}
            >
              <option value="inside">内部</option>
              <option value="center">居中</option>
              <option value="outside">外部</option>
            </select>
          </label>

          <label class={styles.row}>
            <span class={styles.label}>混合模式</span>
            <select
              class={styles.select}
              value={blendMode()}
              data-testid="stroke-blend-mode"
              onChange={(event) => {
                setBlendMode(event.currentTarget.value as SeparableBlendMode);
              }}
            >
              <For each={BLEND_MODE_CHOICES}>
                {(option) => <option value={option.value}>{option.label}</option>}
              </For>
            </select>
          </label>

          <label class={styles.row}>
            <span class={styles.label}>不透明度</span>
            <input
              type="range"
              min="0"
              max="100"
              value={opacity()}
              data-testid="stroke-opacity"
              onInput={(event) => {
                setOpacity(Number(event.currentTarget.value));
              }}
            />
            <span class={styles.value}>{opacity()}%</span>
          </label>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="stroke-confirm"
              onClick={() => {
                props.onStroke({
                  width: width(),
                  color: pickedColor(),
                  position: position(),
                  blendMode: blendMode(),
                  opacity: opacity() / 100,
                });
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
